// Main App component for KATA Dashboard

import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import { DashboardModeProvider } from './contexts/DashboardModeContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import PasswordReset from './pages/PasswordReset';
import Dashboard from './pages/Dashboard';
import Workshops from './pages/Workshops';
import WorkshopDetail from './pages/WorkshopDetail';
import WorkshopForm from './pages/WorkshopForm';
import KATABoard from './components/kata-board/KATABoard';
import KATABoardV2Layout from './components/kata-board-v2/KATABoardV2Layout';
import PlantMapping from './components/plant-mapping/PlantMapping';
import Analytics from './pages/Analytics';
import Planning from './pages/Planning';
import KataCoachingPage from './pages/KataCoachingPage';
import Settings from './pages/Settings';
import Profile from './pages/Profile';

const App: React.FC = () => {
  const { initializeAuth, isLoading } = useAuthStore();

  // Initialize authentication on app startup
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // Show loading screen during initialization
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-sm text-gray-600">Loading KATA Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <DashboardModeProvider>
      <Router>
        <div className="App">
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password/:uid/:token" element={<PasswordReset />} />
          
          {/* Protected Routes */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/workshops" 
            element={
              <ProtectedRoute>
                <Workshops />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/workshops/new" 
            element={
              <ProtectedRoute requireAdmin={true}>
                <WorkshopForm />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/workshops/:id" 
            element={
              <ProtectedRoute>
                <WorkshopDetail />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/workshops/:id/edit" 
            element={
              <ProtectedRoute requireAdmin={true}>
                <WorkshopForm />
              </ProtectedRoute>
            } 
          />
          
              <Route
                path="/workshops/:workshopId/kata-board"
                element={
                  <ProtectedRoute>
                    <KATABoard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/workshops/:workshopId/kata-board-v2"
                element={
                  <ProtectedRoute>
                    <KATABoardV2Layout />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/plant-mapping/:layoutId"
                element={
                  <ProtectedRoute>
                    <PlantMapping />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/plant-mapping"
                element={
                  <ProtectedRoute>
                    <PlantMapping plantId={1} />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/analytics"
                element={
                  <ProtectedRoute>
                    <Analytics />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/planning"
                element={
                  <ProtectedRoute>
                    <Planning />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/kata-coaching"
                element={
                  <ProtectedRoute>
                    <KataCoachingPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/settings"
                element={
                  <ProtectedRoute requireAdmin={true}>
                    <Settings />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
          
          {/* Default redirect */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          
          {/* Catch all - redirect to dashboard */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        </div>
      </Router>
    </DashboardModeProvider>
  );
};

export default App;